/* eslint-disable react/jsx-curly-newline */
import React, { useState, useEffect, useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import { UseFormReturn, Controller } from "react-hook-form";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// Pages
import { IFormConcretingRequestInputs } from "@pages/ConcretingRequest";

// GraphQL
import {
  contractConcreteRecipeAllWithPagination,
  TContractConcreteRecipeAllWithPaginationQueries
} from "src/store/graphql/groups/contractConcreteRecipe/query.graphql";
import {
  ContractConcreteRecipeQueriesGetAllWithPaginationArgs,
  ContractConcreteRecipeType,
  ECommandResultStatus,
  EWhereClausesOperators,
  ConcretingRequestType
} from "@store/graphql/helpers/types.graphql";

// Languages
import useTranslation from "@languages/useTranslation";
import { MuiMessages } from "@languages/interfaces";
import { BaseCardMessages } from "src/languages/interfaces";

// Helpers
import { handleErrorMessage, getDateLocale } from "@helpers/index";

// Libraries
import { TextField, Autocomplete, CircularProgress } from "@libraries/mui";

import * as Styled from "./styles";

interface FormConcretingRequestConcreteRecipeProps {
  controlForms: UseFormReturn<IFormConcretingRequestInputs>;
  currentConcretingRequest: ConcretingRequestType | undefined;
  onlyView: boolean;
}

const FormConcretingRequestConcreteRecipe: React.FC<
  FormConcretingRequestConcreteRecipeProps
> = ({ controlForms, currentConcretingRequest, onlyView }) => {
  const { t } = useTranslation();
  const dateLocale = getDateLocale();
  const { addToast } = useToast();

  const {
    control,
    setValue,
    getValues,
    formState: { errors: formErrors },
    watch
  } = controlForms;

  // watch values
  const watchContract = watch("contract");
  const watchContractConcreteRecipe = watch("contractConcreteRecipe");

  // states for autocomplete concrete concrete recipe
  const [
    openContractConcreteRecipeAutocomplete,
    setOpenContractConcreteRecipeAutocomplete
  ] = useState(false);
  const [
    optionsContractConcreteRecipeAutocomplete,
    setOptionsContractConcreteRecipeAutocomplete
  ] = useState<ContractConcreteRecipeType[]>([]);
  const loadingContractConcreteRecipeAutocomplete = openContractConcreteRecipeAutocomplete
    && optionsContractConcreteRecipeAutocomplete.length === 0;

  // query for contract concrete recipe
  const [
    getContractConcreteRecipes,
    {
      loading: loadingContractContractConcreteRecipes,
      error: errorContractContractConcreteRecipes,
      data: dataContractContractConcreteRecipes
    }
  ] = useLazyQuery<
    TContractConcreteRecipeAllWithPaginationQueries,
    ContractConcreteRecipeQueriesGetAllWithPaginationArgs
  >(
    contractConcreteRecipeAllWithPagination([
      "id",
      "totalVolume",
      "deliveredVolume",
      "concreteRecipe.description"
    ]),
    {
      fetchPolicy: "network-only"
    }
  );

  const loadContractConcreteRecipes = useCallback(async () => {
    const response = dataContractContractConcreteRecipes?.contractConcreteRecipe
      .getAllWithPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      setOptionsContractConcreteRecipeAutocomplete(
        response.result as ContractConcreteRecipeType[]
      );
    }
  }, [dataContractContractConcreteRecipes]);

  useEffect(() => {
    if (watchContract) {
      getContractConcreteRecipes({
        variables: {
          where: [
            {
              property: "contractId",
              operator: EWhereClausesOperators.EQUALS,
              value: watchContract.id
            },
            {
              property: "status",
              operator: EWhereClausesOperators.EQUALS,
              value: "ACTIVE"
            }
          ]
        }
      });
    }
  }, [watchContract, getContractConcreteRecipes]);

  useEffect(() => {
    if (dataContractContractConcreteRecipes) loadContractConcreteRecipes();
    if (errorContractContractConcreteRecipes) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorContractContractConcreteRecipes.message
      });
    }
  }, [
    addToast,
    dataContractContractConcreteRecipes,
    errorContractContractConcreteRecipes,
    loadContractConcreteRecipes
  ]);

  useEffect(() => {
    if (watchContractConcreteRecipe) {
      setValue("contractedQuantity", watchContractConcreteRecipe.totalVolume);
      setValue(
        "balanceAvailable",
        watchContractConcreteRecipe.totalVolume
          - watchContractConcreteRecipe.deliveredVolume
      );

      return;
    }

    setValue("contractedQuantity", 0);
    setValue("balanceAvailable", 0);
  }, [setValue, watchContractConcreteRecipe]);

  useEffect(() => {
    if (!currentConcretingRequest) return;

    setValue(
      "contractConcreteRecipe",
      currentConcretingRequest?.contractConcreteRecipe
    );
    setValue("requestedQuantity", currentConcretingRequest.requestedQuantity);
    setValue(
      "quantityPerConcreteMixer",
      currentConcretingRequest.quantityPerConcreteMixer
    );
    setValue(
      "pieceToBeConcreted",
      currentConcretingRequest?.pieceToBeConcreted || ""
    );
  }, [currentConcretingRequest, setValue]);

  return (
    <Styled.FormContainer>
      <Controller
        name="contractConcreteRecipe"
        control={control}
        defaultValue={null}
        render={({ field }) => (
          <Autocomplete
            {...field}
            onChange={(_ev, item) => field.onChange(item)}
            onOpen={() => setOpenContractConcreteRecipeAutocomplete(true)}
            onClose={() => setOpenContractConcreteRecipeAutocomplete(false)}
            isOptionEqualToValue={(option, valueOption) => option.id === valueOption.id
            }
            getOptionLabel={(option) => `${option.concreteRecipe.description}`}
            open={openContractConcreteRecipeAutocomplete}
            options={optionsContractConcreteRecipeAutocomplete}
            loading={loadingContractConcreteRecipeAutocomplete}
            loadingText={t(MuiMessages.autocompleteLoading)}
            noOptionsText={t(MuiMessages.autocompleteNoOptions)}
            autoHighlight
            disabled={onlyView}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(BaseCardMessages.concreteRecipeText)}
                error={!!formErrors.contractConcreteRecipe}
                helperText={handleErrorMessage(
                  formErrors.contractConcreteRecipe,
                  t
                )}
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingContractConcreteRecipeAutocomplete && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        )}
      />
      <Controller
        name="contractedQuantity"
        control={control}
        defaultValue={0}
        render={({ field }) => (
          <TextField
            {...field}
            type="number"
            autoComplete="off"
            label={t(
              BaseCardMessages.concretingRequestConcretingRecipeContractedQuantityText
            )}
            variant="outlined"
            error={!!formErrors.contractedQuantity}
            helperText={handleErrorMessage(formErrors.contractedQuantity, t)}
            className="default-field"
            size="small"
            disabled
            InputProps={{
              inputProps: {
                min: 0
              }
            }}
          />
        )}
      />
      <Controller
        name="balanceAvailable"
        control={control}
        defaultValue={0}
        render={({ field }) => (
          <TextField
            {...field}
            type="number"
            autoComplete="off"
            label={t(
              BaseCardMessages.concretingRequestConcretingRecipeBalanceAvailableText
            )}
            variant="outlined"
            error={!!formErrors.balanceAvailable}
            helperText={handleErrorMessage(formErrors.balanceAvailable, t)}
            className="default-field"
            size="small"
            disabled
            InputProps={{
              inputProps: {
                min: 0
              }
            }}
          />
        )}
      />
      <Controller
        name="requestedQuantity"
        control={control}
        defaultValue={0}
        render={({ field }) => (
          <TextField
            {...field}
            type="number"
            autoComplete="off"
            label={t(
              BaseCardMessages.concretingRequestConcreteRecipeRequestedQuantityText
            )}
            variant="outlined"
            error={!!formErrors.requestedQuantity}
            helperText={handleErrorMessage(formErrors.requestedQuantity, t)}
            className="default-field"
            disabled={onlyView}
            size="small"
            InputProps={{
              inputProps: {
                min: 0
              }
            }}
          />
        )}
      />
      <Controller
        name="quantityPerConcreteMixer"
        control={control}
        defaultValue={0}
        render={({ field }) => (
          <TextField
            {...field}
            type="number"
            autoComplete="off"
            label={t(
              BaseCardMessages.concretingRequestConcreteRecipeQuantityPerConcreteMixerText
            )}
            variant="outlined"
            error={!!formErrors.quantityPerConcreteMixer}
            helperText={handleErrorMessage(
              formErrors.quantityPerConcreteMixer,
              t
            )}
            className="default-field"
            disabled={onlyView}
            size="small"
            InputProps={{
              inputProps: {
                min: 0
              }
            }}
          />
        )}
      />
      <Controller
        name="pieceToBeConcreted"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            type="text"
            autoComplete="off"
            label={t(
              BaseCardMessages.concretingRequestConcreteRecipePieceToBeConcretedText
            )}
            variant="outlined"
            error={!!formErrors.pieceToBeConcreted}
            helperText={handleErrorMessage(formErrors.pieceToBeConcreted, t)}
            className="default-field"
            disabled={onlyView}
            size="small"
            InputProps={{
              inputProps: {
                min: 0
              }
            }}
          />
        )}
      />
    </Styled.FormContainer>
  );
};

export default FormConcretingRequestConcreteRecipe;
